<template>
  <div class="todayactivity" v-loading="loading">
    <el-form
      ref="formRef"
      :model="formData"
      :rules="rules"
      label-width="150px"
      class="config-form"
    >
      <el-form-item label="今日活动图片" prop="todayActivity">
        <div class="upload-container">
          <el-upload
            class="image-uploader"
            :action="uploadUrl"
            :headers="uploadHeaders"
            name="files"
            :show-file-list="false"
            :on-success="handleActivityUpload"
            :before-upload="beforeUpload"
          >
            <el-image
              v-if="formData.todayActivity"
              :src="getFullUrl(formData.todayActivity)"
              class="uploaded-image"
              fit="contain"
            />
            <el-icon v-else class="upload-icon">
              <Plus />
            </el-icon>
          </el-upload>
          <div class="upload-tip">点击上传今日活动图片</div>
        </div>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSave" :loading="saving">
          保存配置
        </el-button>
        <el-button @click="handleReset" :loading="resetting">
          重置为默认
        </el-button>
      </el-form-item>
    </el-form>
  </div>
</template>
<script lang="ts" setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useApi } from '@/hooks/useApi';
import { PartialStoreNoticeSpe } from '@/api';
import { ElMessage, ElMessageBox, FormInstance, FormRules, UploadRawFile } from 'element-plus';
import { useBaseAsync, useBaseUserStore } from 'hpsfir-managebase-ui';
import { Plus } from '@element-plus/icons-vue';

interface TodayActivityFormData {
  todayActivity: string;
}

interface StoreNoticeData {
  todayActivity?: string;
}

interface UploadResponse {
  data: string[];
}

const formRef = ref<FormInstance>();
const saving = ref(false);
const resetting = ref(false);
const loading = ref(false);
const baseUserStore = useBaseUserStore();

const formData = reactive<TodayActivityFormData>({
  todayActivity: '',
});

const rules: FormRules = {
  todayActivity: [{ required: true, message: '请上传今日活动图片', trigger: 'change' }],
};

const uploadUrl = computed(() => useApi().baseUrl + useApi().uploadFileUrl);

const uploadHeaders = computed(() => ({
  Authorization: baseUserStore.token,
}));

const getFullUrl = (url: string) => {
  if (!url) {
    return '';
  }
  if (url.startsWith('http')) {
    return url;
  }
  return useApi().baseUrl + url;
};

const beforeUpload = (file: UploadRawFile) => {
  const isImage = file.type.startsWith('image/');
  const isLt5M = file.size / 1024 / 1024 < 5;

  if (!isImage) {
    ElMessage.error('只能上传图片文件!');
    return false;
  }
  if (!isLt5M) {
    ElMessage.error('上传图片大小不能超过 5MB!');
    return false;
  }
  return true;
};

const handleActivityUpload = (response: UploadResponse) => {
  if (response && response.data && response.data.length > 0) {
    const [url] = response.data;
    formData.todayActivity = url;
    ElMessage.success('今日活动图片上传成功');
  }
};

const loadConfig = useBaseAsync(async() => {
  loading.value = true;
  try {
    const res = await useApi().storeNoticeApi.getStoreNoticestoreNotice();
    const data = res.data?.data as StoreNoticeData | undefined;
    formData.todayActivity = data?.todayActivity || '';
  } finally {
    loading.value = false;
  }
});

const handleSave = useBaseAsync(async() => {
  await formRef.value?.validate();
  saving.value = true;
  try {
    const storeNotice = {
      todayActivity: formData.todayActivity,
    } as unknown as PartialStoreNoticeSpe;
    await useApi().storeNoticeApi.updateStoreNoticestoreNotice({
      storeNotice,
    });
    ElMessage.success('保存成功');
  } finally {
    saving.value = false;
  }
});

const handleReset = async() => {
  await ElMessageBox.confirm('确定要重置为默认配置吗？', '提示', {
    type: 'warning',
    confirmButtonText: '确认',
    cancelButtonText: '取消',
  });
  resetting.value = true;
  try {
    const storeNotice = {
      todayActivity: '',
    } as unknown as PartialStoreNoticeSpe;
    await useApi().storeNoticeApi.updateStoreNoticestoreNotice({
      storeNotice,
    });
    ElMessage.success('重置成功');
    await loadConfig();
  } finally {
    resetting.value = false;
  }
};

onMounted(() => {
  loadConfig();
});
</script>
<style lang="scss" scoped>
@import './index.scss';
</style>
